import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import CountryCode from "../register/signup/phone/CountryCode";
import { AccountEditProps } from "./EditEmail";
import { useAuth } from "../../context/AuthContext";
import {
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  updatePhoneNumber,
} from "firebase/auth";
import { updateUser } from "../../api/accounts/userServices";
export interface ModerationOption {
  title: string;
  value: string;
}

const EditPhone = ({ close }: AccountEditProps) => {
  const authContext = useAuth();
  const auth = authContext?.auth;
  const [phoneNo, setPhoneNo] = useState("");
  const [selectedCode, setSelectedCode] = useState("+1");
  const [isValidForm, setIsValidForm] = useState(false);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState<RecaptchaVerifier | null>(null);
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState("");
  const phoneRegExp = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

  const schema = Yup.object().shape({
    phone: Yup.string().required("Phone number required").matches(phoneRegExp, "Phone number is not valid").min(10, "too short").max(10, "too long"),
  });

  useEffect(() => {
    // Set up reCAPTCHA verifier for phone authentication
    if (!auth) return;
    const verifier = new RecaptchaVerifier(auth, "recaptcha-container", { size: "invisible" });
    verifier.render();
    setRecaptchaVerifier(verifier);
  }, [auth]);

  const sendPhoneVerificationCode = async () => {
    if (!auth || !auth.currentUser) return;
    try {
      // const phoneNumber = auth.currentUser.phoneNumber;
      const phNumber = selectedCode + phoneNo;
      if (phNumber && recaptchaVerifier) {
        const confirmationResult = await signInWithPhoneNumber(auth, phNumber, recaptchaVerifier);
        setVerificationId(confirmationResult.verificationId);
        setStep(2); // Move to the step to enter the OTP
      }
    } catch (error) {
      console.error("Error sending phone verification code:", error);
    }
  };

  const updateUserPhoneNumber = async () => {
    if (!verificationId || !auth?.currentUser) return;
    try {
      const credential = PhoneAuthProvider.credential(verificationId, otp);
      // await updatePhoneNumber(auth.currentUser, credential);

      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential);
      const phone = selectedCode + phoneNo;
      if (auth.currentUser) {
        const mfaUsr = await multiFactor(auth.currentUser);
        const factors = mfaUsr.enrolledFactors;
        const phoneFactor = factors?.find(factor => factor.factorId === "phone");
        if (phoneFactor?.uid) {
          await mfaUsr.unenroll(phoneFactor?.uid);
        }
        await mfaUsr.enroll(multiFactorAssertion, phone);
        // await setDoc(doc(db, COLLECTIONS.USERS, auth.currentUser.uid), { phone }, { merge: true });
        await updateUser(auth.currentUser.uid, { phone });
      }
      console.log("Phone number updated successfully");
      setStep(3);
    } catch (error) {
      console.error("Error updating phone number:", error);
    }
  };

  useEffect(() => {
    const validate = async (phone: string) => {
      try {
        await schema.validate({ phone }, { abortEarly: true });
        setIsValidForm(true);
      } catch (err) {
        setIsValidForm(false);
      }
    };
    validate(phoneNo);
  }, [phoneNo]);

  const onInputEvent = async (event: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (event.type === "focus") {
      setErrors(prev => {
        const { [name]: _, ...rest } = prev;
        return rest;
      });
      return;
    }
    try {
      await schema.validateAt(name, { [name]: value });
      setErrors(prev => {
        const { [name]: _, ...rest } = prev;
        return rest;
      });
      return true;
    } catch (err) {
      let error = err as Yup.ValidationError;
      setErrors(prev => ({ ...prev, [name]: error.message }));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-50">
      {step === 3 ? (
        <div className="p-6 bg-white rounded-lg shadow-lg w-80">
          <h2 className="mb-4 text-lg font-semibold">Your phone number updated to {phoneNo}</h2>
          <div className="mb-5 flex flex-col space-y-[10px] w-full"></div>
        </div>
      ) : (
        <div className="p-6 bg-white rounded-lg shadow-lg w-80">
          <div id="recaptcha-container"></div>
          <h2 className="mb-4 text-lg font-semibold">Change Phone</h2>
          <div className="mb-5 flex flex-col space-y-[10px] w-full">
            <label htmlFor="phone" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
              Your phone
            </label>
            <CountryCode onCountrySelect={code => setSelectedCode(code)} />
            <input
              type="text"
              name="phone"
              id="phone"
              className="w-full h-[36px] rounded-md border border-gray-300 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onChange={e => {
                setPhoneNo(e.target.value);
              }}
              placeholder="Phone number"
              onBlur={onInputEvent}
              onFocus={onInputEvent}
              value={phoneNo}
              required
            />
            {errors.phone && <span className="block mb-2 text-sm text-red-600 dark:text-red-500">{errors.phone}</span>}
            {step === 2 && (
              <div className="mb-5 flex flex-col space-y-[10px] w-full">
                <label htmlFor="phone" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Enter OTP
                </label>
                <input
                  onBlur={onInputEvent}
                  onFocus={onInputEvent}
                  className="verificationcodetext"
                  type="text"
                  name="otp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="\d{6}"
                  value={otp}
                  onChange={e => setOtp(e.target.value)}
                  placeholder="Enter OTP"
                />
              </div>
            )}
          </div>
          <div className="flex flex-col space-y-2">
            {step === 1 && (
              <button
                className="px-4 py-2 text-black bg-white border border-black rounded-md"
                onClick={sendPhoneVerificationCode}
                disabled={!isValidForm}
              >
                Send Verification Code
              </button>
            )}
            {step === 2 && (
              <button className="px-4 py-2 text-black bg-white border border-black rounded-md" onClick={updateUserPhoneNumber}>
                Verify and Update Phone Number
              </button>
            )}
            <button
              className="px-4 py-2 text-red-600 border border-black rounded-md"
              onClick={() => {
                close();
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditPhone;
